import isUndefined from 'lodash/isUndefined';
import noop from 'lodash/noop';
import omitBy from 'lodash/omitBy';
import { bool, number, object, string } from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { matchPath, Route } from 'react-router';
import { history, location } from 'react-router-prop-types';
import { bindActionCreators } from 'redux';

import api from 'atg/api/API';
import RankingView from 'atg/components/grid/RankingView';
import PresetReduction from 'atg/components/PresetReduction';
import ReductionPaywall from 'atg/components/ReductionPaywall';
import Toolbar from 'atg/components/toolbar';
import { PRESET_REDUCTION_RECALC } from 'atg/constants/BetUpdateType';
import BetHeader from 'atg/containers/betHeader/BetHeaderContainer';
import { changeView } from 'atg/redux/actions';
import { view as viewSelector } from 'atg/redux/selectors';
import { startStompCheck, stopStompCheck } from 'atg/stomp';
import RequestBatch from 'atg/utils/RequestBatch';
import BetBody from 'shared/components/layout/BetBody';
import PageBody from 'shared/components/layout/PageBody';
import PageMeta from 'shared/components/PageMeta';
import * as DialogTypes from 'shared/constants/DialogTypes';
import {
  categories as menuCategories,
  items as menuItems,
} from 'shared/constants/MenuConstants';
import CollaborationContainer from 'shared/containers/CollaborationContainer';
import {
  changeDisabledMenuItems,
  changeMenuCategory,
  clearSelectedMenuItem,
  fetchSettings,
  hideLoader,
  showAtgArchiveDialog,
  showAtgReportDialog,
  showCouponsDialog,
  showLoader,
} from 'shared/redux/actions';
import { fetchMyHorses } from 'shared/redux/actions/monitorActions';
import {
  dialogs,
  menu,
  settings,
  shares as shareSelectors,
  user,
} from 'shared/redux/selectors';

const mapStateToProps = (state, ownProps) => ({
  selectedMenuId: menu.getSelectedId(state),
  isErrorDialogActive: dialogs.hasDialog(state, {
    dialogType: DialogTypes.ERROR,
  }),
  settings: settings.getSettings(state),
  hasViewToolbar: viewSelector.hasViewToolbar(state),
  toolbarContentForView: viewSelector.getToolbarContent(state),
  viewType: viewSelector.getCurrentViewType(state),
  claimStatus: shareSelectors.getClaimStatus(state, {
    betId: ownProps.betId,
    type: 'atg',
  }),
  hasSubscription: user.hasSubscription(state),
  lastBetUpdate: shareSelectors.getLastBetUpdate(state, {
    betId: ownProps.betId,
    type: 'atg',
  }),
  collaborators: shareSelectors.getCollaboratorsList(state, {
    betId: ownProps.betId,
    type: 'atg',
  }),
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      changeView,
      showLoader,
      hideLoader,
      changeMenuCategory,
      changeDisabledMenuItems,
      clearSelectedMenuItem,
      showCouponsDialog,
      showAtgArchiveDialog,
      showAtgReportDialog,
      fetchSettings,
      fetchMyHorses,
    },
    dispatch,
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  class BetContainer extends Component {
    static propTypes = {
      history: history.isRequired,
      location: location.isRequired,
      jokerController: object.isRequired,
      isLoggedIn: bool.isRequired,
      betId: number,
      shareCode: string,
      settings: object,
    };

    defaultState = {
      isBetUpdating: false,
      hasCoupons: false,
      indicator: null,
      betDataInvalid: false,
      error: undefined,
      reductionMenuVisible: false,
    };

    state = this.defaultState;

    async componentDidMount() {
      const {
        changeView,
        jokerController,
        changeMenuCategory,
        fetchSettings,
        fetchMyHorses,
      } = this.props;

      this.hasInitData = false;

      changeMenuCategory(menuCategories.ATG);
      startStompCheck(api);
      jokerController.setupUI(this.mainNode, this.middleNode, this.bottomNode);

      const view = new RankingView(jokerController);

      changeView(view, view.getGridClass());

      jokerController.setUpdateMenuFn(
        ({ betUpdateActive, betHasCoupons } = {}) => {
          const isBetUpdating = isUndefined(betUpdateActive)
            ? this.state.isBetUpdating
            : betUpdateActive;

          const hasCoupons = isUndefined(betHasCoupons)
            ? this.state.hasCoupons
            : betHasCoupons;

          if (
            isBetUpdating !== this.state.isBetUpdating ||
            hasCoupons !== this.state.hasCoupons
          ) {
            this.setState({
              isBetUpdating,
              hasCoupons,
            });
          }
        },
      );

      jokerController.setUpdateTopInfoFn(
        ({ indicator, betDataInvalid, error, reductionMenuVisible } = {}) => {
          this.setState(
            omitBy(
              {
                // Legacy workaround:
                // If the update function call from JokerController results
                // in no changed state we still want the BetHeader component
                // to re-render but because it is connected we need to force
                // a changed state.
                __forcedHeaderUpdate__: Symbol(),
                indicator,
                betDataInvalid,
                error,
                reductionMenuVisible,
              },
              isUndefined,
            ),
          );
        },
      );

      if (INCLUDE_HORSE_MONITORING) {
        fetchMyHorses();
      }
      fetchSettings();
      this.updateMenu();
      this.loadData(this.props);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
      const {
        betId,
        shareCode,
        changeView,
        jokerController,
        selectedMenuId,
        isErrorDialogActive,
        clearSelectedMenuItem,
      } = nextProps;

      if (betId !== this.props.betId || shareCode !== this.props.shareCode) {
        const view = new RankingView(jokerController);

        changeView(view, view.getGridClass());
        this.setState(this.defaultState);
        this.loadData(nextProps);
      }

      if (selectedMenuId !== this.props.selectedMenuId) {
        switch (selectedMenuId) {
          case menuItems.ATG_ARCHIVE:
            nextProps.showAtgArchiveDialog();

            break;

          case menuItems.ATG_REPORT:
            nextProps.showAtgReportDialog();

            break;

          case menuItems.ATG_COUPONS:
            nextProps.showCouponsDialog();

            break;

          case menuItems.ATG_EXPORT:
            jokerController.onMenuExportPressed();

            break;

          case menuItems.ATG_MANUAL:
            clearSelectedMenuItem();
            jokerController.onMenuRecalcBetPressed();

            break;

          default:
          // Do nothing
        }
      }

      // Error when showing export dialog, clear selected menu
      if (
        selectedMenuId === menuItems.ATG_EXPORT &&
        isErrorDialogActive &&
        !this.props.isErrorDialogActive
      ) {
        clearSelectedMenuItem();
      }
    }

    componentDidUpdate(prevProps, prevState) {
      if (
        this.state.isBetUpdating !== prevState.isBetUpdating ||
        this.state.hasCoupons !== prevState.hasCoupons ||
        this.props.isLoggedIn !== prevProps.isLoggedIn ||
        this.props.settings !== prevProps.settings
      ) {
        this.updateMenu();
      }
      // If the update is a SHARED_BET_UPDATE_ATG triggered by another user
      // we should update the bet with that data and re-render
      if (
        this.props.lastBetUpdate &&
        this.props.lastBetUpdate.updater !==
          this.props.collaborators?.find(collaborator => collaborator.self)
            ?.id &&
        (this.props.lastBetUpdate.betAndStake.bet.modifiedAt !==
          prevProps.lastBetUpdate?.betAndStake.bet.modifiedAt ||
          this.props.lastBetUpdate.betAndStake.bet.betConfig.isLocked !==
            prevProps.lastBetUpdate?.betAndStake.bet.betConfig.isLocked)
      ) {
        const deepClonedBetAndStake = JSON.parse(
          JSON.stringify(this.props.lastBetUpdate.betAndStake),
        );
        api.onBetAndStakeUpdate(deepClonedBetAndStake);
      }
      // A change to the claimStatus should trigger any necessary updates
      // in jokerController
      if (this.props.claimStatus !== prevProps.claimStatus) {
        this.props.jokerController.onClaimStatusChanged();
      }
    }

    componentWillUnmount() {
      const { jokerController } = this.props;
      stopStompCheck();
      jokerController.setUpdateMenuFn();
      jokerController.setUpdateTopInfoFn();
    }

    updateMenu() {
      const { isLoggedIn, changeDisabledMenuItems, settings } = this.props;
      const { isBetUpdating, hasCoupons } = this.state;

      changeDisabledMenuItems(
        [
          menuItems.ATG_ARCHIVE,
          menuItems.ATG_COUPONS,
          menuItems.ATG_EXPORT,
          menuItems.ATG_MANUAL,
          menuItems.ATG_SETTINGS,
        ].filter(item => {
          if (!isLoggedIn) {
            return true;
          }
          if (
            (item === menuItems.ATG_COUPONS ||
              item === menuItems.ATG_EXPORT ||
              item === menuItems.ATG_MANUAL) &&
            isBetUpdating
          ) {
            return true;
          }
          if (item === menuItems.ATG_COUPONS && !hasCoupons) {
            return true;
          }
          if (item === menuItems.ATG_SETTINGS && settings == null) {
            return true;
          }
          return false;
        }),
      );
    }

    async loadData(props) {
      const {
        history,
        location,
        jokerController,
        betId,
        shareCode,
        showLoader,
        hideLoader,
      } = props;

      const {
        state: { showBetConfig = false, hasBetInformation = false } = {},
      } = location;

      showLoader('main-loader-load-bet-heading');

      try {
        await new Promise((resolve, reject) => {
          if (shareCode) {
            api.fetchSharedBet(shareCode, success => {
              if (success) {
                resolve();
              } else {
                reject(new Error('Invalid share code'));
              }
            });
          } else if (betId) {
            resolve();
          } else {
            reject(new Error('Missing or invalid share code or bet id'));
          }
        });

        await new Promise((resolve, reject) => {
          if (shareCode || hasBetInformation) {
            resolve();
            return;
          }

          api.fetchBetInformation(betId, success => {
            if (success) {
              resolve();
            } else {
              reject(new Error(`Failed to fetch bet ${betId}`));
            }
          });
        });

        await new Promise(resolve => {
          const requestBatch = new RequestBatch();

          if (!api.betInformation.isEmpty()) {
            requestBatch.addRequest(api.getLoadStakeRequest());
            requestBatch.addRequest(api.getUpdateRaceInformationRequest());
            requestBatch.addRequest(api.getUpdateGameInformationRequest());
            requestBatch.addRequest(api.getUpdateResultInformationRequest());
          }

          requestBatch.onComplete = resolve;
          requestBatch.start();
        });

        if (!this.hasInitData) {
          this.hasInitData = true;
          jokerController.onInitialData();
        }
        history.replace({
          ...location,
          state: undefined,
        });
        if (showBetConfig) {
          jokerController.openBetConfigDialog();
        }
      } catch (error) {
        console.error(error);
      } finally {
        hideLoader();
      }
    }

    onMainRef = ref => {
      this.mainNode = ref;
    };

    onMiddleRef = ref => {
      this.middleNode = ref;
    };

    onBottomRef = ref => {
      this.bottomNode = ref;
    };

    render() {
      const {
        betId,
        hasViewToolbar,
        jokerController,
        viewType,
        collaborators,
        claimStatus,
        hasSubscription,
        toolbarContentForView: {
          pointReductionIndex = 0,
          getTrendMinutes = noop,
        } = {},
      } = this.props;

      const hasCollaboration = collaborators?.length >= 2;

      const myId = collaborators?.find(collaborator => collaborator.self)?.id;
      const claimedByAnotherUser =
        claimStatus?.claimed && claimStatus.currentOwner !== myId;
      const isReadOnlyUser = collaborators?.some(
        collaborator => collaborator.self && collaborator.type === 'READ',
      );

      const { indicator } = this.state;

      const { betInformation, raceInformation } = api;
      const betConfig = betInformation.getBetConfig();
      const isLocked = betConfig.isLocked();
      const trendTime = hasViewToolbar
        ? betConfig.getTrendFormattedTimestamp()
        : '';
      const pointSourceData = betInformation.data.pointSource || {};
      const pointSource = pointSourceData[pointReductionIndex];

      const betKey = betInformation.getBetKey();
      const betType = betKey && betKey.getBetType();
      const presetReduction = betInformation.getPresetReduction();

      const onPresetReductionUpdate = presetReduction => {
        betInformation.setPresetReduction(presetReduction);
        api.scheduleBetUpdate(PRESET_REDUCTION_RECALC);
      };

      const isOnUpgradePath =
        matchPath(this.props.location.pathname, {
          path: '/trav/:betId/uppgradera',
        })?.isExact === true;

      return (
        <PageBody fullWidth alignTo="edge">
          <PageMeta key="meta" theme="atg" />
          {!betInformation.isEmpty() && (
            <BetHeader jokerController={jokerController} {...this.state} />
          )}
          <BetBody
            theme="atg"
            hasCollaboration={hasCollaboration}
            isPaddingBottomView={viewType === 'view-factor-reduction'}
            refHandler={this.onMainRef}
          >
            {hasViewToolbar && (
              <Toolbar
                betType={betType}
                betInformation={betInformation}
                getTrendMinutes={getTrendMinutes}
                isLocked={
                  isLocked ||
                  (hasCollaboration &&
                    (claimedByAnotherUser ||
                      isReadOnlyUser ||
                      !hasSubscription))
                }
                onChangeTrendTime={jokerController.onChangeTrendTime}
                onResetOwnWinPercentages={
                  jokerController.onResetOwnWinPercentages
                }
                onSetPointDisplay={jokerController.setPointDisplay}
                pointReductionIndex={pointReductionIndex}
                pointSource={pointSource}
                raceInformation={raceInformation}
                trendTime={trendTime}
                viewType={viewType}
              />
            )}

            {viewType === 'view-preset-reduction' && (
              <PresetReduction
                betType={betType}
                isLoading={indicator === 'spinner'}
                presetReduction={presetReduction}
                onPresetReductionUpdate={onPresetReductionUpdate}
                showAlertWhenLocked={jokerController.showAlertWhenLocked}
              />
            )}

            <div
              className="middle"
              style={isOnUpgradePath ? { display: 'none' } : undefined}
              ref={this.onMiddleRef}
            />
            <div
              className="bottom"
              style={isOnUpgradePath ? { display: 'none' } : undefined}
              ref={this.onBottomRef}
            />
            <Route
              path="/trav/:betId/uppgradera"
              exact
              component={ReductionPaywall}
            />
          </BetBody>
          {betId && !isOnUpgradePath && (
            <CollaborationContainer type="atg" betId={betId} />
          )}
        </PageBody>
      );
    }
  },
);
